import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAdminSpeakers,
  updateAdminSpeaker,
  Speaker,
  UpdateSpeakerPayload,
} from '../../lib/api';
import {
  Users,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Sparkles,
  Calendar,
  MapPin,
  Mic,
  Plus,
  Edit3,
  ExternalLink,
  Linkedin,
  FileText,
  Building2,
  Save,
  RefreshCw,
  X,
  Layers,
  ArrowRight,
  ChevronRight,
  Eye,
} from 'lucide-react';

const TRACK_PRESETS = [
  'Biologicals & Biofertilizers',
  'Agrochemicals & Crop Protection',
  'Agritech, AI & Precision Farming',
  'Policy, Global Trade & Market Access',
  'Post-Harvest & Cold Chain Innovations',
  'Sustainable Formulation Chemistry',
];

const HALL_PRESETS = [
  'Main Plenary Hall',
  'Conference Hall 1',
  'Conference Hall 2',
  'Technical Breakout A',
  'Technical Breakout B',
  'Exhibition Presentation Stage',
];

const DATE_PRESETS = [
  { value: '2027-07-27', label: 'Day 1 — 27 July 2027' },
  { value: '2027-07-28', label: 'Day 2 — 28 July 2027' },
];

function timeToMinutes(timeStr?: string | null): number | null {
  if (!timeStr) return null;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function hasTimeOverlap(
  startA?: string | null,
  endA?: string | null,
  startB?: string | null,
  endB?: string | null
): boolean {
  const sA = timeToMinutes(startA);
  const eA = timeToMinutes(endA);
  const sB = timeToMinutes(startB);
  const eB = timeToMinutes(endB);

  if (sA === null || eA === null || sB === null || eB === null) return false;
  return sA < eB && eA > sB;
}

export function AdminSpeakersModule({ adminKey }: { adminKey: string }) {
  const queryClient = useQueryClient();
  const [statusTab, setStatusTab] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'matrix'>('table');
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<'profile' | 'schedule'>('profile');
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Form state for modal editor
  const [editForm, setEditForm] = useState<UpdateSpeakerPayload>({});

  const {
    data: speakers = [],
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<Speaker[]>({
    queryKey: ['admin-speakers', adminKey, statusTab],
    queryFn: () => fetchAdminSpeakers(adminKey, statusTab),
    enabled: !!adminKey,
  });

  // Mutation for updating speaker
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSpeakerPayload }) =>
      updateAdminSpeaker(id, payload, adminKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-speakers'] });
      queryClient.invalidateQueries({ queryKey: ['public-speakers'] });
      queryClient.invalidateQueries({ queryKey: ['featured-speakers'] });
      setActionMessage(`Speaker "${data.speaker.fullName}" updated successfully.`);
      setIsEditorOpen(false);
      setSelectedSpeaker(null);
    },
    onError: (err: any) => {
      setActionMessage(`Error updating speaker: ${err.message}`);
    },
  });

  // Calculate conflict matrix across all approved speakers
  const schedulingConflicts = useMemo(() => {
    const conflicts: { [speakerId: string]: Speaker[] } = {};

    const scheduled = speakers.filter(
      (s) =>
        s.status === 'approved' &&
        s.stageOrHall &&
        s.sessionDate &&
        s.sessionStartTime &&
        s.sessionEndTime
    );

    for (let i = 0; i < scheduled.length; i++) {
      for (let j = i + 1; j < scheduled.length; j++) {
        const a = scheduled[i];
        const b = scheduled[j];

        if (
          a.stageOrHall === b.stageOrHall &&
          a.sessionDate === b.sessionDate &&
          hasTimeOverlap(a.sessionStartTime, a.sessionEndTime, b.sessionStartTime, b.sessionEndTime)
        ) {
          conflicts[a.id] = [...(conflicts[a.id] || []), b];
          conflicts[b.id] = [...(conflicts[b.id] || []), a];
        }
      }
    }

    return conflicts;
  }, [speakers]);

  // Check live conflict inside modal editor
  const currentModalConflict = useMemo(() => {
    if (!selectedSpeaker) return null;

    const targetHall = editForm.stageOrHall ?? selectedSpeaker.stageOrHall;
    const targetDate = editForm.sessionDate ?? selectedSpeaker.sessionDate;
    const targetStart = editForm.sessionStartTime ?? selectedSpeaker.sessionStartTime;
    const targetEnd = editForm.sessionEndTime ?? selectedSpeaker.sessionEndTime;

    if (!targetHall || !targetDate || !targetStart || !targetEnd) return null;

    const conflictSpeaker = speakers.find((s) => {
      if (s.id === selectedSpeaker.id) return false;
      if (s.status !== 'approved') return false;
      if (s.stageOrHall !== targetHall) return false;
      if (s.sessionDate !== targetDate) return false;

      return hasTimeOverlap(targetStart, targetEnd, s.sessionStartTime, s.sessionEndTime);
    });

    return conflictSpeaker || null;
  }, [selectedSpeaker, editForm, speakers]);

  const handleOpenEditor = (speaker: Speaker, initialTab: 'profile' | 'schedule' = 'profile') => {
    setSelectedSpeaker(speaker);
    setEditForm({
      fullName: speaker.fullName,
      email: speaker.email,
      phone: speaker.phone,
      organization: speaker.organization,
      designation: speaker.designation,
      bio: speaker.bio,
      headshotUrl: speaker.headshotUrl,
      linkedinUrl: speaker.linkedinUrl,
      topicTitle: speaker.topicTitle,
      topicAbstract: speaker.topicAbstract,
      track: speaker.track,
      status: speaker.status,
      isFeatured: speaker.isFeatured,
      stageOrHall: speaker.stageOrHall,
      sessionDate: speaker.sessionDate,
      sessionStartTime: speaker.sessionStartTime,
      sessionEndTime: speaker.sessionEndTime,
    });
    setEditorTab(initialTab);
    setIsEditorOpen(true);
  };

  const handleQuickStatus = (speaker: Speaker, newStatus: 'under_review' | 'approved' | 'rejected') => {
    updateMutation.mutate({
      id: speaker.id,
      payload: { status: newStatus },
    });
  };

  const handleSaveModal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSpeaker) return;
    updateMutation.mutate({
      id: selectedSpeaker.id,
      payload: editForm,
    });
  };

  // Filtered speakers
  const filteredSpeakers = speakers.filter((s) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      s.fullName.toLowerCase().includes(q) ||
      s.organization.toLowerCase().includes(q) ||
      s.topicTitle.toLowerCase().includes(q) ||
      s.track.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q)
    );
  });

  const countByStatus = {
    all: speakers.length,
    submitted: speakers.filter((s) => s.status === 'submitted').length,
    under_review: speakers.filter((s) => s.status === 'under_review').length,
    approved: speakers.filter((s) => s.status === 'approved').length,
    rejected: speakers.filter((s) => s.status === 'rejected').length,
  };

  return (
    <div className="space-y-6">
      {/* Module Title & Quick Stats Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-black text-slate-900">
              Speaker Management & Session Scheduling Matrix
            </h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] border border-[#0A3D27]/20">
              ADMIN CONSOLE
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Evaluate call-for-speakers proposals, refine speaker profiles, and assign stage sessions with conflict detection.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Submissions Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('matrix')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                viewMode === 'matrix' ? 'bg-[#0A3D27] text-white shadow-xs' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Scheduling Matrix
            </button>
          </div>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-2 bg-white hover:bg-slate-50 text-slate-600 rounded-xl border border-slate-200 transition-colors cursor-pointer"
            title="Refresh speaker roster"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin text-[#0A3D27]' : ''}`} />
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
            <span className="font-semibold">{actionMessage}</span>
          </div>
          <button
            onClick={() => setActionMessage(null)}
            className="text-slate-400 hover:text-slate-700 text-xs cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filter Tabs & Search Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 flex-wrap">
        {/* Status Tabs */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200 overflow-x-auto text-xs font-bold">
          <button
            type="button"
            onClick={() => setStatusTab('all')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              statusTab === 'all' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All Submissions ({countByStatus.all})
          </button>
          <button
            type="button"
            onClick={() => setStatusTab('submitted')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              statusTab === 'submitted' ? 'bg-amber-500 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Submitted ({countByStatus.submitted})
          </button>
          <button
            type="button"
            onClick={() => setStatusTab('under_review')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              statusTab === 'under_review' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Under Review ({countByStatus.under_review})
          </button>
          <button
            type="button"
            onClick={() => setStatusTab('approved')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              statusTab === 'approved' ? 'bg-[#0A3D27] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Approved ({countByStatus.approved})
          </button>
          <button
            type="button"
            onClick={() => setStatusTab('rejected')}
            className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              statusTab === 'rejected' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Rejected ({countByStatus.rejected})
          </button>
        </div>

        {/* Search */}
        <div className="relative min-w-[260px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-2.5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by speaker, topic, or track..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27]"
          />
        </div>
      </div>

      {/* VIEW MODE 1: SUBMISSIONS TABLE */}
      {viewMode === 'table' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                  <th className="py-3 px-4">Speaker & Org</th>
                  <th className="py-3 px-4">Topic & Track</th>
                  <th className="py-3 px-4">Review Status</th>
                  <th className="py-3 px-4">Session Schedule</th>
                  <th className="py-3 px-4 text-right">Quick Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      Loading speaker proposals...
                    </td>
                  </tr>
                ) : filteredSpeakers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400">
                      No speaker submissions found matching current criteria.
                    </td>
                  </tr>
                ) : (
                  filteredSpeakers.map((speaker) => {
                    const hasConflict = !!schedulingConflicts[speaker.id]?.length;

                    return (
                      <tr key={speaker.id} className="hover:bg-slate-50/60 transition-colors">
                        {/* Speaker & Org */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center font-bold text-slate-500 text-xs">
                              {speaker.headshotUrl ? (
                                <img
                                  src={speaker.headshotUrl}
                                  alt={speaker.fullName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                speaker.fullName.slice(0, 2).toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-slate-900 hover:text-[#0A3D27] cursor-pointer" onClick={() => handleOpenEditor(speaker)}>
                                  {speaker.fullName}
                                </span>
                                {speaker.isFeatured && (
                                  <span className="p-0.5 text-amber-500" title="Featured Spotlight Keynote">
                                    <Sparkles className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500">{speaker.designation}</p>
                              <p className="text-[10px] text-slate-400 font-medium">{speaker.organization}</p>
                            </div>
                          </div>
                        </td>

                        {/* Topic & Track + Deck & AV Requirements */}
                        <td className="py-3.5 px-4 max-w-xs">
                          <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] px-2 py-0.5 rounded-full border border-[#E6C687] inline-block mb-1">
                            {speaker.track}
                          </span>
                          <div className="font-bold text-slate-800 line-clamp-1">{speaker.topicTitle}</div>
                          <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">
                            {speaker.topicAbstract}
                          </p>

                          {/* Uploaded Presentation Slide Deck & AV Requirements (Decisions D5 & D11) */}
                          <div className="flex items-center gap-1.5 flex-wrap mt-2">
                            {(speaker as any).deckUrl ? (
                              <a
                                href={(speaker as any).deckUrl}
                                download={(speaker as any).deckFileName || `${speaker.fullName}-deck`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] border border-emerald-200 text-[10px] font-bold transition-colors"
                                title="Download Uploaded Presentation Slide Deck"
                              >
                                <FileText className="w-3 h-3 shrink-0" />
                                <span className="truncate max-w-[140px]">
                                  {(speaker as any).deckFileName || 'Download Slide Deck'}
                                </span>
                                <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                              </a>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-400 border border-slate-200 text-[10px] font-medium">
                                <FileText className="w-3 h-3 shrink-0" />
                                <span>No Deck Uploaded</span>
                              </span>
                            )}

                            {Array.isArray((speaker as any).avRequirements) &&
                              (speaker as any).avRequirements.map((av: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-800 border border-purple-200 text-[10px] font-bold"
                                >
                                  <Mic className="w-2.5 h-2.5 shrink-0" />
                                  <span>{av}</span>
                                </span>
                              ))}
                            {typeof (speaker as any).avRequirements === 'string' &&
                              (speaker as any).avRequirements.trim() &&
                              (speaker as any).avRequirements
                                .split(',')
                                .map((s: string) => s.trim())
                                .filter(Boolean)
                                .map((av: string, idx: number) => (
                                  <span
                                    key={idx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-800 border border-purple-200 text-[10px] font-bold"
                                  >
                                    <Mic className="w-2.5 h-2.5 shrink-0" />
                                    <span>{av}</span>
                                  </span>
                                ))}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          {speaker.status === 'submitted' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              <Clock className="w-3 h-3" />
                              Submitted
                            </span>
                          )}
                          {speaker.status === 'under_review' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                              <Eye className="w-3 h-3" />
                              Under Review
                            </span>
                          )}
                          {speaker.status === 'approved' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-[#0A3D27] border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3" />
                              Approved
                            </span>
                          )}
                          {speaker.status === 'rejected' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                              <XCircle className="w-3 h-3" />
                              Rejected
                            </span>
                          )}
                        </td>

                        {/* Schedule */}
                        <td className="py-3.5 px-4">
                          {speaker.stageOrHall ? (
                            <div className="space-y-1">
                              <div className="font-bold text-[#0A3D27] flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {speaker.stageOrHall}
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {speaker.sessionDate ? `${speaker.sessionDate.slice(5)} • ` : ''}
                                {speaker.sessionStartTime} - {speaker.sessionEndTime}
                              </div>
                              {hasConflict && (
                                <div className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Time Conflict!
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenEditor(speaker, 'schedule')}
                              className="text-[11px] font-semibold text-[#0A3D27] hover:underline cursor-pointer flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Assign Stage Slot
                            </button>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            {speaker.status === 'submitted' && (
                              <button
                                type="button"
                                onClick={() => handleQuickStatus(speaker, 'under_review')}
                                className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-[11px] font-bold transition-colors cursor-pointer border border-blue-200"
                              >
                                Review
                              </button>
                            )}

                            {speaker.status === 'under_review' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleQuickStatus(speaker, 'approved')}
                                  className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] rounded-lg text-[11px] font-bold transition-colors cursor-pointer border border-emerald-200"
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleQuickStatus(speaker, 'rejected')}
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-[11px] font-bold transition-colors cursor-pointer border border-rose-200"
                                >
                                  Reject
                                </button>
                              </>
                            )}

                            <button
                              type="button"
                              onClick={() => handleOpenEditor(speaker)}
                              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer border border-slate-200"
                              title="Edit Profile & Schedule"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW MODE 2: SCHEDULING MATRIX TIMELINE */}
      {viewMode === 'matrix' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {DATE_PRESETS.map((day) => {
              const daySpeakers = speakers.filter(
                (s) => s.status === 'approved' && s.sessionDate === day.value && s.stageOrHall
              );

              return (
                <div key={day.value} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                    <div className="flex items-center gap-2 text-slate-900 font-black text-sm">
                      <Calendar className="w-4 h-4 text-[#0A3D27]" />
                      <span>{day.label}</span>
                    </div>
                    <span className="text-[11px] font-bold text-slate-500">
                      {daySpeakers.length} Session{daySpeakers.length === 1 ? '' : 's'} Allocated
                    </span>
                  </div>

                  {daySpeakers.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-xs">
                      No sessions currently scheduled for this conference date.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {daySpeakers
                        .sort((a, b) => (a.sessionStartTime || '').localeCompare(b.sessionStartTime || ''))
                        .map((spk) => {
                          const hasConflict = !!schedulingConflicts[spk.id]?.length;

                          return (
                            <div
                              key={spk.id}
                              className={`p-3.5 rounded-xl border transition-all text-xs flex items-start justify-between gap-3 ${
                                hasConflict
                                  ? 'bg-rose-50 border-rose-300 ring-1 ring-rose-400'
                                  : 'bg-[#F8F9F6] border-slate-200 hover:border-[#0A3D27]/40'
                              }`}
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-bold text-[#0A3D27] bg-white px-2 py-0.5 rounded border border-slate-200 text-[11px]">
                                    {spk.sessionStartTime} - {spk.sessionEndTime}
                                  </span>
                                  <span className="font-bold text-slate-800">{spk.stageOrHall}</span>
                                </div>
                                <h4 className="font-bold text-slate-900 text-sm mt-1">{spk.fullName}</h4>
                                <p className="text-[11px] text-slate-600 font-medium">{spk.topicTitle}</p>
                                <div className="flex items-center gap-2 pt-1 text-[10px] text-slate-500">
                                  <span>{spk.organization}</span>
                                  <span>•</span>
                                  <span className="text-[#785613]">{spk.track}</span>
                                </div>

                                {/* Deck & AV badges on matrix card */}
                                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                  {(spk as any).deckUrl && (
                                    <a
                                      href={(spk as any).deckUrl}
                                      download={(spk as any).deckFileName || `${spk.fullName}-deck`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] border border-emerald-200 text-[10px] font-bold"
                                    >
                                      <FileText className="w-3 h-3" />
                                      <span>{(spk as any).deckFileName || 'Slide Deck'}</span>
                                    </a>
                                  )}
                                  {Array.isArray((spk as any).avRequirements) &&
                                    (spk as any).avRequirements.map((av: string, idx: number) => (
                                      <span
                                        key={idx}
                                        className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-800 border border-purple-200 text-[10px] font-bold"
                                      >
                                        {av}
                                      </span>
                                    ))}
                                </div>

                                {hasConflict && (
                                  <div className="text-[11px] font-bold text-rose-700 flex items-center gap-1.5 pt-1">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                    <span>
                                      Stage Overlap Conflict with:{' '}
                                      {schedulingConflicts[spk.id].map((c) => c.fullName).join(', ')}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() => handleOpenEditor(spk, 'schedule')}
                                className="p-1.5 bg-white text-slate-600 hover:text-[#0A3D27] rounded-lg border border-slate-200 cursor-pointer shrink-0"
                                title="Reschedule Session"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MODAL: SPEAKER PROFILE BUILDER & SESSION SCHEDULER */}
      {isEditorOpen && selectedSpeaker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-base font-black text-slate-900">
                  Speaker Profile Builder & Session Scheduling
                </h3>
                <p className="text-xs text-slate-500">
                  Editing profile: <strong className="text-slate-800">{selectedSpeaker.fullName}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditorOpen(false)}
                className="p-1.5 rounded-xl hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Navigation Tabs */}
            <div className="flex border-b border-slate-200 px-5 pt-3 gap-3 bg-white">
              <button
                type="button"
                onClick={() => setEditorTab('profile')}
                className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                  editorTab === 'profile'
                    ? 'border-[#0A3D27] text-[#0A3D27]'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                1. Profile & Abstract
              </button>
              <button
                type="button"
                onClick={() => setEditorTab('schedule')}
                className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
                  editorTab === 'schedule'
                    ? 'border-[#0A3D27] text-[#0A3D27]'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <span>2. Session Scheduling Matrix</span>
                {currentModalConflict && (
                  <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                )}
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSaveModal} className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
              {/* TAB 1: PROFILE BUILDER */}
              {editorTab === 'profile' && (
                <div className="space-y-4">
                  {/* Status & Featured Banner */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[#F8F9F6] p-4 rounded-2xl border border-slate-200">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Application Review Status
                      </label>
                      <select
                        value={editForm.status || 'submitted'}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            status: e.target.value as any,
                          }))
                        }
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 cursor-pointer"
                      >
                        <option value="submitted">Submitted</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div className="flex flex-col justify-center">
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Spotlight Feature
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer mt-1">
                        <input
                          type="checkbox"
                          checked={!!editForm.isFeatured}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              isFeatured: e.target.checked,
                            }))
                          }
                          className="w-4 h-4 text-[#0A3D27] rounded border-slate-300 focus:ring-[#0A3D27]"
                        />
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                          Feature in Homepage Keynotes
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Speaker Full Name</label>
                      <input
                        type="text"
                        value={editForm.fullName || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, fullName: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Organization</label>
                      <input
                        type="text"
                        value={editForm.organization || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, organization: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Designation</label>
                      <input
                        type="text"
                        value={editForm.designation || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, designation: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Speaker Bio</label>
                    <textarea
                      rows={3}
                      value={editForm.bio || ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, bio: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 resize-y"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Headshot Image URL</label>
                      <input
                        type="url"
                        value={editForm.headshotUrl || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, headshotUrl: e.target.value }))}
                        placeholder="https://example.com/headshot.jpg"
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">LinkedIn URL</label>
                      <input
                        type="url"
                        value={editForm.linkedinUrl || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, linkedinUrl: e.target.value }))}
                        placeholder="https://linkedin.com/in/username"
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Conference Track</label>
                    <select
                      value={editForm.track || TRACK_PRESETS[0]}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, track: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 cursor-pointer"
                    >
                      {TRACK_PRESETS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Topic Title</label>
                    <input
                      type="text"
                      value={editForm.topicTitle || ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, topicTitle: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Topic Abstract</label>
                    <textarea
                      rows={4}
                      value={editForm.topicAbstract || ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, topicAbstract: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 resize-y"
                      required
                    />
                  </div>
                </div>
              )}

              {/* TAB 2: SESSION SCHEDULER & CONFLICT DETECTION */}
              {editorTab === 'schedule' && (
                <div className="space-y-5">
                  {/* LIVE VISUAL CONFLICT WARNING BANNER */}
                  {currentModalConflict && (
                    <div className="p-4 bg-rose-50 border-2 border-rose-300 rounded-2xl flex items-start gap-3 text-rose-900 animate-in fade-in">
                      <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <h4 className="font-black text-xs uppercase tracking-wide text-rose-700">
                          ⚠️ Stage & Time Slot Conflict Detected!
                        </h4>
                        <p className="text-xs leading-relaxed">
                          <strong>{currentModalConflict.fullName}</strong> is already allocated to{' '}
                          <strong>{currentModalConflict.stageOrHall}</strong> from{' '}
                          <span className="font-mono font-bold">
                            {currentModalConflict.sessionStartTime} – {currentModalConflict.sessionEndTime}
                          </span>{' '}
                          on {currentModalConflict.sessionDate}.
                        </p>
                        <p className="text-[11px] text-rose-600">
                          Please shift this session to a different stage or non-overlapping time window.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-[#FAF5EA] border border-[#E6C687] rounded-2xl p-4 text-slate-700 space-y-1">
                    <span className="font-bold text-[#785613] block">Stage Allocation Rules</span>
                    <p className="text-[11px]">
                      Plenary addresses are normally 45 minutes (JWCC Main Plenary Hall). Technical panel sessions
                      are 30–45 minutes in Conference Halls 1 & 2.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Assigned Hall / Stage
                      </label>
                      <select
                        value={editForm.stageOrHall || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, stageOrHall: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 cursor-pointer font-bold"
                      >
                        <option value="">-- Unassigned Stage --</option>
                        {HALL_PRESETS.map((hall) => (
                          <option key={hall} value={hall}>
                            {hall}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Session Date
                      </label>
                      <select
                        value={editForm.sessionDate || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, sessionDate: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 cursor-pointer font-bold"
                      >
                        <option value="">-- Select Date --</option>
                        {DATE_PRESETS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Session Start Time (24h)
                      </label>
                      <input
                        type="time"
                        value={editForm.sessionStartTime || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, sessionStartTime: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-900"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Session End Time (24h)
                      </label>
                      <input
                        type="time"
                        value={editForm.sessionEndTime || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, sessionEndTime: e.target.value }))}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-900"
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() =>
                        setEditForm((prev) => ({
                          ...prev,
                          stageOrHall: null,
                          sessionDate: null,
                          sessionStartTime: null,
                          sessionEndTime: null,
                        }))
                      }
                      className="text-xs text-slate-500 hover:text-rose-600 transition-colors cursor-pointer"
                    >
                      Clear Schedule Allocation
                    </button>
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-6 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{updateMutation.isPending ? 'Saving...' : 'Save Speaker Profile'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSpeakersModule;
